import { useState, useEffect } from 'react';
import Logo from '../components/Logo';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PAYSTACK_KEY      = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY     || 'pk_live_1ccf892eeebb25711a45869c389b6187b027ee44';
const KORAPAY_KEY       = process.env.REACT_APP_KORAPAY_PUBLIC_KEY      || 'pk_live_2zPyMPiFrfuwBTMwpWThNW4asrbBGfbL8YCxCMH3';
const FLUTTERWAVE_KEY   = process.env.REACT_APP_FLUTTERWAVE_PUBLIC_KEY  || 'FLWPUBK-0dd962ca68ec2002da8b7cdff7f45235-X';
const WHATSAPP_NUMBER   = '2349115294976';
const BUSINESS_EMAIL    = 'info@PilotLeadGen.com';
const NGN_RATE          = 1600;

const PLANS = [
  {
    id:'free', name:'Free Plan', priceUSD:0, priceNGN:0, priceGBP:0,
    period:'', badge:null, color:'#64748b',
    desc:'Perfect for beginners setting up their Google Ads account.',
    features:[
      { text:'Google Ads account setup guide',     included:true  },
      { text:'7-step beginner setup (baby steps)', included:true  },
      { text:'Conversion tracking setup guide',    included:true  },
      { text:'Account readiness checklist',        included:true  },
      { text:'Campaign builder',                   included:false },
      { text:'AI keywords & ad copy',              included:false },
      { text:'Click-by-click launch guide',        included:false },
    ],
    cta:'Start for Free', ctaStyle:'outline',
  },
  {
    id:'starter', name:'Starter Plan', priceUSD:49, priceNGN:49*NGN_RATE, priceGBP:39,
    period:'/month', badge:'⭐ Most Popular', color:'#6366f1',
    desc:'Everything you need to build, launch and track your first campaign.',
    features:[
      { text:'Everything in Free',              included:true },
      { text:'Smart Setup Wizard',              included:true },
      { text:'Full Campaign Builder',           included:true },
      { text:'AI keywords & ad copy generator', included:true },
      { text:'Click-by-click Launch Guide',     included:true },
      { text:'Results tracking & insights',     included:true },
      { text:'Unlimited campaigns',             included:true },
    ],
    cta:'Get Starter Plan', ctaStyle:'solid',
  },
  {
    id:'pro', name:'Pro Plan', priceUSD:99, priceNGN:99*NGN_RATE, priceGBP:79,
    period:'/month', badge:null, color:'#7c3aed',
    desc:'Full power for businesses ready to scale.',
    features:[
      { text:'Everything in Starter',          included:true },
      { text:'YouTube Ads campaign builder',   included:true },
      { text:'Full template library',          included:true },
      { text:'Industry keyword packs',         included:true },
      { text:'Landing page templates',         included:true },
      { text:'AI optimisation suggestions',    included:true },
      { text:'Priority support',               included:true },
    ],
    cta:'Get Pro Plan', ctaStyle:'solid',
  },
  {
    id:'premium', name:'Premium Package', priceUSD:300, priceNGN:300*NGN_RATE, priceGBP:239,
    period:' one-time', badge:'🏆 Done For You', color:'#059669',
    desc:'We handle everything for you — end to end.',
    features:[
      { text:'Full Google Ads account setup',    included:true },
      { text:'Complete campaign build & launch', included:true },
      { text:'Landing page creation',            included:true },
      { text:'GTM conversion tracking setup',    included:true },
      { text:'Campaign optimisation',            included:true },
      { text:'1-on-1 strategy call',             included:true },
      { text:'Dedicated campaign manager',       included:true },
    ],
    cta:'Book a Call', ctaStyle:'premium',
  },
];

function formatPrice(plan, currency) {
  if (plan.priceUSD === 0) return 'Free';
  if (currency === 'NGN') return `₦${plan.priceNGN.toLocaleString()}`;
  if (currency === 'GBP') return `£${plan.priceGBP}`;
  return `$${plan.priceUSD}`;
}

export default function PlanSelection() {
  const navigate              = useNavigate();
  const location              = useLocation();
  const { user, upgradePlan } = useAuth();
  const userPlan = user?.user_metadata?.plan || 'free';
  const isUpgrade = new URLSearchParams(location.search).get('upgrade') === 'true';

  // If user already has a paid plan, send them to dashboard — no need to see plans again
  useEffect(() => {
    if (userPlan && userPlan !== 'free' && !paidPlan) {
      navigate('/dashboard');
    }
  }, [userPlan]); // eslint-disable-line
  const [currency, setCurrency]               = useState('USD');
  const [paidPlan, setPaidPlan]               = useState(null);
  const [paystackReady, setPaystackReady]     = useState(false);
  const [korapayReady, setKorapayReady]       = useState(false);
  const [flutterwaveReady, setFlutterwaveReady] = useState(false);

  // ── Load Paystack script ─────────────────────────────────────────
  useEffect(() => {
    if (window.PaystackPop) { setPaystackReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://js.paystack.co/v1/inline.js';
    s.async = true;
    s.onload = () => { setPaystackReady(true); };
    document.body.appendChild(s);
  }, []);

  // ── Load Korapay script ──────────────────────────────────────────
  useEffect(() => {
    if (window.Korapay || window.KoraPay) { setKorapayReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://korablobstorage.blob.core.windows.net/modal-bucket/korapay-collections.min.js';
    s.async = true;
    s.onload = () => { setKorapayReady(true); };
    document.body.appendChild(s);
  }, []);

  // ── Load Flutterwave script ──────────────────────────────────────
  useEffect(() => {
    if (window.FlutterwaveCheckout) { setFlutterwaveReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.flutterwave.com/v3.js';
    s.async = true;
    s.onload = () => { setFlutterwaveReady(true); };
    document.body.appendChild(s);
  }, []);

  // ── Upgrade plan after payment ───────────────────────────────────
  useEffect(() => {
    if (!paidPlan) return;
    upgradePlan(paidPlan).catch(console.error);
  }, [paidPlan]); // eslint-disable-line

  // ── Handle button clicks ─────────────────────────────────────────
  const handleClick = (plan) => {
    // Free plan
    if (plan.id === 'free') {
      setPaidPlan('free');
      return;
    }

    // Premium — WhatsApp
    if (plan.id === 'premium') {
      const msg = encodeURIComponent(
        `Hello PilotLeadGen! I am interested in the Premium Package ($300 one-time). I would like to book a call to discuss setting up my Google Ads account, campaign, landing page and GTM conversion tracking.`
      );
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
      return;
    }

    // NGN → Paystack
    if (currency === 'NGN') {
      if (!window.PaystackPop) { alert('Paystack is loading. Please try again in a moment.'); return; }
      setTimeout(() => {
        try {
          const handler = window.PaystackPop.setup({
            key:      PAYSTACK_KEY,
            email:    user?.email || 'user@example.com',
            amount:   plan.priceNGN * 100,
            currency: 'NGN',
            ref:      `PLG_${plan.id}_${Date.now()}`,
            callback: () => { setPaidPlan(plan.id); },
            onClose:  () => {},
          });
          handler.openIframe();
        } catch (err) {
          console.error('Paystack error:', err);
          alert('Could not open Paystack. Please refresh and try again.');
        }
      }, 50);
      return;
    }

    // USD → Korapay
    if (currency === 'USD') {
      const koraPay = window.Korapay || window.KoraPay;
      if (!koraPay) { alert('Korapay is loading. Please try again in a moment.'); return; }
      try {
        koraPay.initialize({
          key:       KORAPAY_KEY,
          reference: `PLG_${plan.id}_${Date.now()}`,
          amount:    plan.priceUSD,
          currency:  'USD',
          customer:  { email: user?.email || 'user@example.com', name: user?.user_metadata?.name || 'Customer' },
          onSuccess: () => { setPaidPlan(plan.id); },
          onClose:   () => {},
        });
      } catch (err) {
        console.error('Korapay error:', err);
        alert('Could not open Korapay. Please refresh and try again.');
      }
      return;
    }

    // GBP / AED / Other → Flutterwave
    if (!window.FlutterwaveCheckout) { alert('Flutterwave is loading. Please try again in a moment.'); return; }
    try {
      window.FlutterwaveCheckout({
        public_key:   FLUTTERWAVE_KEY,
        tx_ref:       `PLG_${plan.id}_${Date.now()}`,
        amount:       currency === 'GBP' ? plan.priceGBP : plan.priceUSD,
        currency:     currency,
        customer: {
          email:      user?.email || 'user@example.com',
          name:       user?.user_metadata?.name || 'Customer',
        },
        customizations: {
          title:      'PilotLeadGen',
          description: `${plan.name} — ${plan.period}`,
          logo:       'https://app.pilotleadgen.com/pilotleadgen-logo.png',
        },
        callback: (response) => {
          console.log('Flutterwave success:', response);
          setPaidPlan(plan.id);
        },
        onclose: () => {},
      });
    } catch (err) {
      console.error('Flutterwave error:', err);
      alert('Could not open Flutterwave. Please refresh and try again.');
    }
  };

  // ── Success screen ───────────────────────────────────────────────
  if (paidPlan) {
    const isFree = paidPlan === 'free';
    return (
      <div style={{ minHeight:'100vh', background:'linear-gradient(160deg,#f8faff,#eef2ff)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div style={{ background:'#fff', borderRadius:20, border:'1px solid #e2e8f0', padding:'48px 40px', maxWidth:480, width:'100%', textAlign:'center', boxShadow:'0 4px 40px rgba(99,102,241,0.1)' }}>
          <div style={{ fontSize:64, marginBottom:20 }}>🎉</div>
          <h2 style={{ fontSize:28, fontWeight:800, color:'#0f172a', marginBottom:12 }}>
            {isFree ? 'Welcome to PilotLeadGen!' : 'Payment Successful!'}
          </h2>
          <p style={{ fontSize:15, color:'#64748b', fontFamily:'sans-serif', lineHeight:1.65, marginBottom:32 }}>
            {isFree
              ? "Your free account is ready. Let's walk you through setting up your Google Ads account first."
              : `Your ${paidPlan} plan is now active. Let's build your first campaign!`}
          </p>
          <button
            style={{ width:'100%', padding:'16px', borderRadius:12, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif', boxShadow:'0 4px 20px rgba(99,102,241,0.35)' }}
            onClick={() => isFree ? navigate('/onboarding') : navigate('/dashboard')}
          >
            {isFree ? 'Start Setup Guide →' : 'Go to Dashboard →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Main page ────────────────────────────────────────────────────
  const paystackStatus = paystackReady ? 'ready ✅' : 'loading…';
  const korapayStatus  = korapayReady  ? 'ready ✅' : 'loading…';

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <Logo size={36} showText={true} textSize={18} />
      </div>

      {/* Hero */}
      <div style={s.hero}>
        <div style={s.heroBadge}>{isUpgrade ? '🚀 Upgrade Your Plan' : '🎉 Account Created Successfully'}</div>
        <h1 style={s.heroTitle}>{isUpgrade ? 'Choose Your Upgrade' : 'Choose Your Plan'}</h1>
        <p style={s.heroSub}>{isUpgrade ? 'Unlock the full campaign builder and start running real Google Ads.' : 'Start free and upgrade when you\'re ready. No hidden fees.'}</p>
        {!isUpgrade && <p style={s.heroNote}>💡 Free plan gives full access to the Google Ads setup guide — no card needed.</p>}
      </div>

      {/* Currency toggle */}
      <div style={s.toggleWrap}>
        <p style={s.toggleLabel}>Select your preferred currency:</p>
        <div style={s.toggleRow}>
          {[
            { val:'NGN', flag:'🇳🇬', label:'Naira (NGN)' },
            { val:'USD', flag:'🌍', label:'USD — Korapay' },
            { val:'GBP', flag:'🇬🇧', label:'GBP / AED — Flutterwave' },
          ].map(opt => (
            <button key={opt.val} style={{
              ...s.toggleBtn,
              background: currency === opt.val ? '#6366f1' : '#fff',
              color:      currency === opt.val ? '#fff'    : '#475569',
              border:     currency === opt.val ? '2px solid #6366f1' : '2px solid #e2e8f0',
              fontWeight: currency === opt.val ? 700 : 500,
            }} onClick={() => setCurrency(opt.val)}>
              {opt.flag} {opt.label}
            </button>
          ))}
        </div>
        <p style={s.toggleNote}>
          {currency === 'NGN'
            ? `🔒 Powered by Paystack — ${paystackReady ? 'ready ✅' : 'loading…'}`
            : currency === 'USD'
            ? `🔒 Powered by Korapay — ${korapayReady ? 'ready ✅' : 'loading…'}`
            : `🔒 Powered by Flutterwave — ${flutterwaveReady ? 'ready ✅' : 'loading…'}`}
        </p>
      </div>

      {/* Plan cards */}
      <div style={s.grid}>
        {PLANS.filter(plan => isUpgrade ? plan.id !== 'free' : true).map(plan => {
          const displayPrice = formatPrice(plan, currency);
          const isPaid       = plan.id !== 'free' && plan.id !== 'premium';
          const notReady     = isPaid && (
            currency === 'NGN' ? !paystackReady :
            currency === 'USD' ? !korapayReady :
            !flutterwaveReady
          );
          const bgColor      = plan.ctaStyle === 'solid'
            ? `linear-gradient(135deg,${plan.color},${plan.color}cc)`
            : plan.ctaStyle === 'premium'
            ? 'linear-gradient(135deg,#059669,#10b981)'
            : 'transparent';

          return (
            <div key={plan.id} style={{
              ...s.card,
              borderColor: plan.badge ? plan.color : '#e2e8f0',
              boxShadow:   plan.badge ? `0 8px 32px ${plan.color}25` : '0 2px 12px rgba(0,0,0,0.06)',
              transform:   plan.id === 'starter' ? 'scale(1.03)' : 'scale(1)',
            }}>
              {plan.badge && <div style={{ ...s.badge, background: plan.color }}>{plan.badge}</div>}

              <div style={s.planName}>{plan.name}</div>
              <div style={s.priceRow}>
                <span style={{ ...s.price, color: plan.priceUSD === 0 ? '#64748b' : plan.color }}>
                  {displayPrice}
                </span>
                {plan.priceUSD > 0 && <span style={s.period}>{plan.period}</span>}
              </div>
              <p style={s.planDesc}>{plan.desc}</p>
              <div style={s.divider} />

              <div style={s.features}>
                {plan.features.map((f, i) => (
                  <div key={i} style={s.featureRow}>
                    <span style={{ ...s.featureIcon, color: f.included ? '#059669' : '#cbd5e1' }}>
                      {f.included ? '✓' : '✗'}
                    </span>
                    <span style={{ ...s.featureTxt, color: f.included ? '#334155' : '#94a3b8' }}>
                      {f.text}
                    </span>
                  </div>
                ))}
              </div>

              <button
                disabled={notReady}
                style={{
                  ...s.ctaBtn,
                  background: bgColor,
                  color:   plan.ctaStyle === 'outline' ? plan.color : '#fff',
                  border:  plan.ctaStyle === 'outline' ? `2px solid ${plan.color}` : 'none',
                  opacity: notReady ? 0.5 : 1,
                  cursor:  notReady ? 'not-allowed' : 'pointer',
                }}
                onClick={() => handleClick(plan)}
              >
                {notReady ? 'Loading payment…' : plan.cta}
              </button>

              {plan.id === 'premium' && (
                <div style={s.premiumNote}>
                  <div style={s.premiumNoteRow}><span>📞</span><span>WhatsApp: +{WHATSAPP_NUMBER}</span></div>
                  <div style={s.premiumNoteRow}>
                    <span>📧</span>
                    <a href={`mailto:${BUSINESS_EMAIL}`} style={s.emailLink}>{BUSINESS_EMAIL}</a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reassurance */}
      <div style={s.bottomRow}>
        {[['🔒','Secure — Paystack & Korapay'],['↩️','Cancel anytime'],['💬','Support via WhatsApp & email']].map(([icon,text],i)=>(
          <div key={i} style={s.reassureItem}>
            <span style={{ fontSize:16 }}>{icon}</span>
            <span style={s.reassureTxt}>{text}</span>
          </div>
        ))}
      </div>

      <p style={s.skipNote}>
        Not sure yet?{' '}
        <span style={s.skipLink} onClick={() => handleClick({ id:'free' })}>
          Start with the free plan →
        </span>
      </p>
    </div>
  );
}

const s = {
  page:          { minHeight:'100vh', background:'linear-gradient(160deg,#f8faff 0%,#eef2ff 55%,#f0fdf4 100%)', fontFamily:"'Georgia',serif", paddingBottom:60 },
  header:        { display:'flex', alignItems:'center', justifyContent:'center', padding:'20px 24px', background:'#fff', borderBottom:'1px solid #e2e8f0' },
  logo:          { display:'flex', alignItems:'center', gap:10 },
  mark:          { width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', fontWeight:800, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'sans-serif' },
  logoTxt:       { fontSize:18, fontWeight:800, color:'#1e293b', fontFamily:'sans-serif' },
  hero:          { textAlign:'center', padding:'40px 24px 20px', maxWidth:680, margin:'0 auto' },
  heroBadge:     { display:'inline-block', fontSize:12, fontWeight:700, color:'#059669', background:'#dcfce7', border:'1px solid #bbf7d0', padding:'6px 16px', borderRadius:20, fontFamily:'sans-serif', marginBottom:16 },
  heroTitle:     { fontSize:36, fontWeight:800, color:'#0f172a', margin:'0 0 12px', lineHeight:1.15 },
  heroSub:       { fontSize:16, color:'#475569', fontFamily:'sans-serif', lineHeight:1.65, margin:'0 0 10px' },
  heroNote:      { fontSize:13, color:'#64748b', fontFamily:'sans-serif', fontStyle:'italic' },
  toggleWrap:    { textAlign:'center', padding:'16px 24px 28px', maxWidth:560, margin:'0 auto' },
  toggleLabel:   { fontSize:14, fontWeight:600, color:'#334155', fontFamily:'sans-serif', marginBottom:12 },
  toggleRow:     { display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', marginBottom:10 },
  toggleBtn:     { padding:'12px 22px', borderRadius:12, fontSize:14, cursor:'pointer', fontFamily:'sans-serif', transition:'all 0.2s' },
  toggleNote:    { fontSize:12, color:'#64748b', fontFamily:'sans-serif', fontStyle:'italic', marginTop:8 },
  grid:          { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:20, maxWidth:1100, margin:'0 auto', padding:'0 20px 32px' },
  card:          { background:'#fff', borderRadius:20, border:'2px solid', padding:'28px 24px', display:'flex', flexDirection:'column', position:'relative', transition:'all 0.2s' },
  badge:         { position:'absolute', top:-13, left:'50%', transform:'translateX(-50%)', color:'#fff', fontSize:11, fontWeight:800, padding:'4px 16px', borderRadius:20, fontFamily:'sans-serif', whiteSpace:'nowrap' },
  planName:      { fontSize:13, fontWeight:700, color:'#94a3b8', fontFamily:'sans-serif', textTransform:'uppercase', letterSpacing:1, marginBottom:10 },
  priceRow:      { display:'flex', alignItems:'baseline', gap:4, marginBottom:8 },
  price:         { fontSize:36, fontWeight:800, fontFamily:'sans-serif', lineHeight:1 },
  period:        { fontSize:14, color:'#94a3b8', fontFamily:'sans-serif' },
  planDesc:      { fontSize:13, color:'#64748b', fontFamily:'sans-serif', lineHeight:1.55, marginBottom:18 },
  divider:       { height:1, background:'#f1f5f9', marginBottom:18 },
  features:      { display:'flex', flexDirection:'column', gap:10, flex:1, marginBottom:22 },
  featureRow:    { display:'flex', alignItems:'flex-start', gap:10 },
  featureIcon:   { fontWeight:800, fontSize:14, flexShrink:0, marginTop:1 },
  featureTxt:    { fontSize:13, fontFamily:'sans-serif', lineHeight:1.4 },
  ctaBtn:        { width:'100%', padding:'14px', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif', boxShadow:'0 4px 14px rgba(0,0,0,0.1)', transition:'all 0.2s' },
  premiumNote:   { marginTop:14, display:'flex', flexDirection:'column', gap:6, background:'#f0fdf4', borderRadius:10, padding:'12px 14px', border:'1px solid #bbf7d0' },
  premiumNoteRow:{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#166534', fontFamily:'sans-serif' },
  emailLink:     { color:'#059669', fontWeight:600, textDecoration:'none', fontSize:12, fontFamily:'sans-serif' },
  bottomRow:     { display:'flex', justifyContent:'center', gap:32, flexWrap:'wrap', padding:'20px 24px', maxWidth:700, margin:'0 auto' },
  reassureItem:  { display:'flex', alignItems:'center', gap:8 },
  reassureTxt:   { fontSize:13, color:'#64748b', fontFamily:'sans-serif' },
  skipNote:      { textAlign:'center', fontSize:13, color:'#94a3b8', fontFamily:'sans-serif', paddingBottom:40 },
  skipLink:      { color:'#6366f1', fontWeight:700, cursor:'pointer', textDecoration:'underline' },
};